import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { canTransition, RESOURCE_SESSION_TRANSITIONS } from '@/lib/domain';
import { formatCurrency, formatDuration } from '@/lib/i18n';
import { usePermission } from '@/Hooks/usePermission';
import { meterFor, useMeterTick, type ResourceRow, type SessionRow } from '../lib/sessions';
import { ContextMenu, type MenuAnchor, type MenuRow } from './ContextMenu';
import type { ResourceAction } from './ResourceDialogs';
import { useKidsOccupancy } from './kids-area';

interface ResourceMenuRow extends MenuRow {
  action: ResourceAction;
  /** The meter this row acts on, for the rows that act on one. */
  session: SessionRow | null;
}

interface Props {
  resource: ResourceRow;
  /** Every live session on this resource (§11 — a kids area has several). */
  sessions: SessionRow[];
  anchor: MenuAnchor;
  /** Whether this operator may read sessions at all. */
  canReadSessions: boolean;
  /** True while the session lists are still in flight, so the menu never lies. */
  pending: boolean;
  currency: string;
  onPick: (action: ResourceAction, session: SessionRow | null) => void;
  onClose: () => void;
}

/**
 * §5.2 / §10 — the actions for a resource live on the resource.
 *
 * A PS5 station, a billiards table and a kids area are places where people sit
 * and consume, and the venue already draws them on the floor map. Tapping one
 * used to do nothing at all while the real work sat on a separate page; this is
 * that work, under the pointer, on the same shell tables use.
 *
 * Every entry mirrors what the API would actually accept: the permission key
 * `resource-sessions.controller.ts` enforces, and — for anything that moves a
 * meter — `RESOURCE_SESSION_TRANSITIONS`, the same machine the service asserts
 * on. Starting is the one move with no transition to check, because there is no
 * session yet; its preconditions are the resource's own (`§11` capacity, and a
 * station in maintenance takes nobody).
 *
 * When several guests share one resource, per-session rows would be ambiguous —
 * *whose* meter is being stopped? Those are not offered here; the panel shows a
 * row per child with its own timer, its own coverage and its own check-out.
 */
export function ResourceContextMenu({
  resource,
  sessions,
  anchor,
  canReadSessions,
  pending,
  currency,
  onPick,
  onClose,
}: Props) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();

  const isKids = resource.type === 'KIDS_AREA';
  // §11 — a kids area answers for its own capacity and queue; the server's
  // count includes sessions this screen has not read yet, so it wins.
  const { occupancy } = useKidsOccupancy(resource.id, isKids && canReadSessions, sessions.length);

  const capacity = Math.max(1, occupancy?.capacity ?? resource.capacity);
  const occupied = occupancy?.occupied ?? sessions.length;

  // The meter only has to tick while this menu is open and something is running.
  const running = sessions.some((session) => session.status === 'ACTIVE');
  const now = useMeterTick(running);

  // One live session is unambiguous, so it is what the per-session rows act on.
  const only = sessions.length === 1 ? sessions[0] : null;

  const subtitle = useMemo(() => {
    const parts: string[] = [];
    const status = running ? 'IN_USE' : sessions.length ? 'PAUSED' : resource.status;
    parts.push(t(`spaces.resources.status.${status}`, status));
    if (capacity > 1) parts.push(t('spaces.resources.occupancy', { used: occupied, capacity }));
    if (only) {
      const meter = meterFor(only, now);
      parts.push(formatDuration(meter.seconds));
      if (meter.remaining !== null) parts.push(`−${formatDuration(meter.remaining)}`);
      if (meter.amount !== null) parts.push(formatCurrency(meter.amount, currency, i18n.language));
    }
    if (occupancy && occupancy.waitingCount > 0) {
      parts.push(t('spaces.kids.waitingCount', { count: occupancy.waitingCount }));
    }
    return parts.join(' · ');
  }, [
    capacity,
    currency,
    i18n.language,
    now,
    occupancy,
    occupied,
    only,
    resource.status,
    running,
    sessions.length,
    t,
  ]);

  const items = useMemo<ResourceMenuRow[]>(() => {
    const rows: ResourceMenuRow[] = [];
    const pendingWhy = pending ? t('spaces.menu.sessionsPending') : undefined;
    // Several meters on one resource: acting on "the" session is meaningless,
    // so those rows are not offered — the panel has one per guest.
    const ambiguous = sessions.length > 1 ? t('spaces.menu.pickSessionInPanel') : undefined;

    /**
     * ADR-003 / S-17 — food and drink for the guest on the meter, first,
     * because it is the whole point of doing this from the map: the order is
     * posted against the check the session was opened against, so a PS5's
     * snacks land on that meter's own bill without a table anywhere in sight.
     */
    if (can('orders.create') && only) {
      rows.push({
        action: 'order',
        session: only,
        label: t('spaces.resources.orderFood'),
        disabled: !only.checkId,
        // A meter started with no table and no open check has nothing to bill.
        reason: only.checkId ? undefined : t('spaces.menu.orderNeedsCheck'),
      });
    }

    // §11 — capacity, not "is it free": a shared area is startable until its
    // last place is taken, and a station in maintenance takes nobody.
    if (can('resource_sessions.start')) {
      const unavailable =
        !resource.isActive ||
        resource.status === 'MAINTENANCE' ||
        resource.status === 'OUT_OF_SERVICE'
          ? t('spaces.menu.resourceUnavailable')
          : undefined;
      const full = occupied >= capacity ? t('spaces.menu.resourceFull') : undefined;
      rows.push({
        action: 'start',
        session: null,
        label: isKids ? t('spaces.resources.checkIn') : t('resource.start'),
        disabled: Boolean(unavailable ?? full ?? pendingWhy),
        reason: unavailable ?? full ?? pendingWhy,
      });
    }

    if (
      only &&
      only.status === 'ACTIVE' &&
      canTransition(RESOURCE_SESSION_TRANSITIONS, only.status, 'PAUSED') &&
      can('resource_sessions.pause')
    ) {
      rows.push({ action: 'pause', session: only, label: t('resource.pause') });
    }

    if (
      only &&
      only.status === 'PAUSED' &&
      canTransition(RESOURCE_SESSION_TRANSITIONS, only.status, 'ACTIVE') &&
      can('resource_sessions.resume')
    ) {
      rows.push({ action: 'resume', session: only, label: t('resource.resume') });
    }

    // Extending is not a state move — it widens the planned window — so it is
    // offered on any live meter, which is exactly what the endpoint accepts.
    if (only && can('resource_sessions.extend')) {
      rows.push({ action: 'extend', session: only, label: t('resource.extend') });
    }

    if (
      only &&
      canTransition(RESOURCE_SESSION_TRANSITIONS, only.status, 'ENDED') &&
      can('resource_sessions.stop')
    ) {
      rows.push({
        action: 'stop',
        session: only,
        label: isKids ? t('spaces.resources.checkOut') : t('resource.stop'),
        tone: 'danger',
      });
    }

    // §11 — the wristband is the identifier at the desk, so a band can be
    // checked out without picking the child off a list first.
    if (isKids && sessions.length > 0 && can('resource_sessions.stop')) {
      rows.push({
        action: 'checkOutByTag',
        session: null,
        label: t('spaces.kids.checkOutByTag'),
      });
    }

    // Always last and never disabled: the meters, the pricing rules, the queue
    // and — when several guests share this resource — the only place the
    // per-guest actions exist.
    rows.push({
      action: 'details',
      session: null,
      label: t('spaces.menu.resourceDetails'),
      reason: ambiguous,
    });

    return rows;
  }, [can, capacity, isKids, occupied, only, pending, resource, sessions.length, t]);

  return (
    <ContextMenu
      title={resource.name}
      subtitle={subtitle}
      anchor={anchor}
      items={items}
      footnote={canReadSessions ? null : t('spaces.menu.sessionsUnreadable')}
      onPick={(item) => onPick(item.action, item.session)}
      onClose={onClose}
    />
  );
}
